import React, { useState, useRef, useEffect } from "react";
import "./PasswordResetWindow.css";
import { ModalWrapper } from "./ModalWindow";
import LoadingIcon from "./LoadingIcon";
import GameLogo from "../Resources/logo.png";
import AnimatedCheckmark from "./AnimatedCheckmark";
import { useSendCooldown } from "./useSendCooldown";

// 인증 메일 발송 정책 안내 문구 (서버: IP/이메일당 30분에 1회)
const MAIL_LIMIT_NOTICE = "※ 인증 메일은 30분에 한 번만 전송할 수 있습니다.";

type PasswordResetWindowProps = {
  handleDismiss: () => void;
  onError: (err: string) => void;
  openLoginWindow: () => void;
};

// Define steps for password reset process
type ResetStep = "inputUserEmail" | "inputCode" | "inputNewPassword" | "completed";

export default function PasswordResetWindow({ handleDismiss, onError, openLoginWindow }: PasswordResetWindowProps) {
  const [step, setStep] = useState<ResetStep>("inputUserEmail"); // Track current step
  const [loading, setLoading] = useState(false); // Use 'loading' for general async operation state
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [invalidUsername, setInvalidUsername] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [invalidNewPassword, setInvalidNewPassword] = useState(false);
  const [invalidConfirmNewPassword, setInvalidConfirmNewPassword] = useState(false);

  const [code, setCode] = useState<string>("");
  const [timer, setTimer] = useState<number | null>(null);

  const [username, setUsername] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>("");

  // UseRef for timer interval ID
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 전송 중 중복 클릭 방지 + 전송 후 재클릭 쿨다운
  const inFlightRef = useRef(false);
  const { remaining: cooldown, start: startCooldown, check: checkCooldown } = useSendCooldown();

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  // Basic email validation function (reused)
  function validateEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  // Handlers for input changes
  function handleUsernameChange(event: React.ChangeEvent<HTMLInputElement>) {
    setUsername(event.target.value);
    setInvalidUsername(false);
  }

  function handleEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newEmail = event.target.value;
    setEmail(newEmail);
    setInvalidEmail(!validateEmail(newEmail) && newEmail.length > 0);
    // 입력한 이메일에 대한 재전송 쿨다운을 확인해 버튼에 반영
    checkCooldown(validateEmail(newEmail) ? newEmail : "");
  }

  function handleCodeChange(event: React.ChangeEvent<HTMLInputElement>) {
    setCode(event.target.value);
  }

  function handleNewPasswordChange(event: React.ChangeEvent<HTMLInputElement>) {
    setNewPassword(event.target.value);
    setPasswordMismatch(false);
    setInvalidNewPassword(false);
  }

  function handleConfirmNewPasswordChange(event: React.ChangeEvent<HTMLInputElement>) {
    setConfirmNewPassword(event.target.value);
    setPasswordMismatch(false);
    setInvalidConfirmNewPassword(false);
  }

  // Timer start function (reused and slightly modified)
  function startTimer() {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    const initialCountdown = 300; // 5 minutes
    setTimer(initialCountdown);

    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer === null || prevTimer <= 1) {
          clearInterval(interval);
          timerIntervalRef.current = null;
          // 인증 시간 만료 시 에러 메시지 표시
          onError("인증 시간이 만료되었습니다. 인증번호 재전송을 시도해주세요.");
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);

    timerIntervalRef.current = interval;
  }

  // Handle Username/Email submission (Step 1)
  async function handleUserEmailSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loading || inFlightRef.current) return;

    const isEmailValid = validateEmail(email);
    const isUsernameValid = username.trim().length > 0;

    setInvalidEmail(!isEmailValid && email.length > 0);
    setInvalidUsername(!isUsernameValid);

    if (!isUsernameValid) {
      onError("아이디를 입력해주세요.");
      return;
    }
    if (!email || !isEmailValid) {
      onError("올바른 이메일 주소를 입력해주세요.");
      return;
    }

    // 클라이언트 재전송 쿨다운 확인 (연속 전송 방지)
    const remainingCooldown = checkCooldown(email);
    if (remainingCooldown > 0) {
      onError(`인증 메일을 이미 전송했습니다. 약 ${remainingCooldown}초 후에 다시 시도할 수 있습니다.`);
      return;
    }

    inFlightRef.current = true;
    setLoading(true);
    setInvalidEmail(false);
    setInvalidUsername(false);

    try {
      // 1. Verify Username and Email
      const verifyResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/verifyEmail`, {
        method: "POST",
        body: JSON.stringify({ email }), // Assuming verifyEmail takes email
        headers: { "Content-Type": "application/json" },
      });

      if (!verifyResponse.ok) {
        // 서버 응답이 200-299 범위가 아닌 경우 처리
        let errorMsg = `사용자 정보 확인 중 서버 오류 발생 (${verifyResponse.status})`;
        try {
          const errorData = await verifyResponse.json();
          errorMsg = errorData.msg || errorMsg;
          console.error("verifyEmail API 오류 (JSON 응답):", verifyResponse.status, errorData.msg);
        } catch (jsonError) {
          console.error("verifyEmail API 오류 (Non-JSON 응답):", verifyResponse.status, verifyResponse.statusText);
        }
        onError(errorMsg);
        setLoading(false);
        return;
      }

      const verifyData = await verifyResponse.json();

      // Check if the returned username matches the input username
      if (!verifyData.userInfo || !verifyData.userInfo.userName || verifyData.userInfo.userName !== username.trim()) {
        // onError("입력하신 아이디와 이메일이 일치하는 사용자 정보를 찾을 수 없습니다.");
        onError("입력하신 정보가 올바르지 않습니다.");
        setLoading(false);
        return;
      }

      // 2. Generate and Save Auth Code (setAuthCode)
      const authCode = Math.floor(100000 + Math.random() * 900000).toString();
      const authCodeTime = 300; // 5 minutes

      const setAuthCodeResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/setAuthCode`, {
        method: "POST",
        body: JSON.stringify({ email, authCode, authCodeTime }),
        headers: { "Content-Type": "application/json" },
      });

      if (!setAuthCodeResponse.ok) {
        // 서버 응답이 200-299 범위가 아닌 경우 처리
        let errorMsg = `인증 코드 저장 중 서버 오류 발생 (${setAuthCodeResponse.status})`;
        try {
          const errorData = await setAuthCodeResponse.json();
          errorMsg = errorData.msg || errorMsg;
          console.error("setAuthCode API 오류 (JSON 응답):", setAuthCodeResponse.status, errorData.msg);
        } catch (jsonError) {
          console.error("setAuthCode API 오류 (Non-JSON 응답):", setAuthCodeResponse.status, setAuthCodeResponse.statusText);
        }
        onError(errorMsg);
        setLoading(false);
        return;
      }

      const setAuthCodeData = await setAuthCodeResponse.json();
      // setAuthCode API 응답에 대한 추가적인 성공/실패 확인 로직 필요 시 여기에 추가

      // 3. Send Email (sendMail)
      const sendMailResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sendMail`, {
        method: "POST",
        body: JSON.stringify({ email, authCode }),
        headers: { "Content-Type": "application/json" },
      });

      if (!sendMailResponse.ok) {
        // 서버 응답이 200-299 범위가 아닌 경우 처리
        let errorMsg = `인증 이메일 발송 중 오류가 발생했습니다. (${sendMailResponse.status})`;
        let retryAfter = 0;
        try {
          const errorData = await sendMailResponse.json();
          errorMsg = errorData.msg || errorMsg;
          retryAfter = errorData.retryAfter || 0;
          console.error("sendMail API 오류 (JSON 응답):", sendMailResponse.status, errorData.msg);
        } catch (jsonError) {
          console.error("sendMail API 오류 (Non-JSON 응답):", sendMailResponse.status, sendMailResponse.statusText);
        }
        // 서버 발송 제한(30분에 1회)에 걸린 경우
        if (sendMailResponse.status === 429) {
          const mins = retryAfter ? Math.ceil(retryAfter / 60) : 30;
          startCooldown(email);
          onError(`인증 메일은 30분에 한 번만 전송할 수 있습니다. 약 ${mins}분 후에 다시 시도해주세요.`);
        } else {
          onError(errorMsg);
        }
        setLoading(false);
        return;
      }

      const sendMailData = await sendMailResponse.json();
      // sendMail API 응답에 대한 추가적인 성공/실패 확인 로직 필요 시 여기에 추가

      // All steps successful, start cooldown and transition to Step 2: Input Code
      startCooldown(email);
      setStep("inputCode");
      startTimer(); // Start the timer
      setCode(""); // Clear previous code input
      onError(""); // Clear previous error message
    } catch (error) {
      console.error("사용자/이메일 확인 및 인증 코드 발송 중 알 수 없는 오류 발생:", error);
      onError("알 수 없는 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }

  // Handle Code submission (Step 2)
  async function handleCodeSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loading) return;

    if (!email) {
      onError("이메일 정보가 없습니다. 다시 시도해주세요.");
      return;
    }
    if (!code.trim()) {
      onError("인증번호를 입력하세요.");
      return;
    }

    setLoading(true);

    try {
      // Check Auth Code (getAuthCode)
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/getAuthCode`, {
        method: "POST",
        body: JSON.stringify({ email }), // Assuming getAuthCode takes email
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        let errorMsg = `인증 코드 확인 중 서버 오류 발생 (${response.status})`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.msg || errorMsg;
          console.error("getAuthCode API 오류 (JSON 응답):", response.status, errorData.msg);
        } catch (jsonError) {
          console.error("getAuthCode API 오류 (Non-JSON 응답):", response.status, response.statusText);
        }
        onError(errorMsg);
        setLoading(false);
        return;
      }

      const data = await response.json();

      // Assuming API returns code in 'msg' for getAuthCode
      const apiAuthCode = String(data.msg || "").trim();
      const inputAuthCode = String(code || "").trim();

      if (apiAuthCode === inputAuthCode && apiAuthCode !== "" && timer !== null && timer > 0) {
        // Code is valid and within time limit
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
          setTimer(null); // Stop and reset timer state
        }
        setStep("inputNewPassword"); // Transition to Step 3
        setNewPassword(""); // Clear password fields
        setConfirmNewPassword("");
        onError(""); // Clear previous error message
      } else if (timer !== null && timer <= 0) {
        // Timer expired - error handled by timer interval
        // Make sure to stop timer if not already stopped
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        // onError is already called by the timer interval
        setTimer(0); // Ensure timer state is 0
      } else {
        // Code mismatch
        onError("인증번호가 일치하지 않습니다.");
      }
    } catch (error) {
      console.error("인증 코드 확인 API 호출 중 예외 발생:", error);
      onError("인증 코드 확인 중 알 수 없는 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }

  // Handle Password submission (Step 3)
  async function handlePasswordSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loading) return;

    // Validation
    const isNewPasswordEmpty = newPassword.trim().length === 0;
    const isConfirmPasswordEmpty = confirmNewPassword.trim().length === 0;
    const passwordsMatch = newPassword === confirmNewPassword;

    setInvalidNewPassword(isNewPasswordEmpty);
    setInvalidConfirmNewPassword(isConfirmPasswordEmpty);
    setPasswordMismatch(!passwordsMatch && !isNewPasswordEmpty && !isConfirmPasswordEmpty); // Only show mismatch if both fields are not empty

    if (isNewPasswordEmpty) {
      onError("새 비밀번호를 입력해주세요.");
      return;
    }
    if (isConfirmPasswordEmpty) {
      onError("새 비밀번호 확인을 입력해주세요.");
      return;
    }
    if (!passwordsMatch) {
      onError("입력된 비밀번호가 일치하지 않습니다.");
      return;
    }

    setLoading(true);
    setPasswordMismatch(false); // Clear mismatch error if validation passes

    try {
      // Call the reset password API
      const resetPasswordResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/resetPassword`, {
        method: "POST",
        body: JSON.stringify({
          username: username, // Use the username stored from step 1
          email: email, // Use the email stored from step 1
          password: newPassword,
          password2: confirmNewPassword,
          authCode: code,
        }),
        headers: { "Content-Type": "application/json" },
      });

      // Handle API responses based on user provided messages
      const resetPasswordData = await resetPasswordResponse.json();

      if (resetPasswordResponse.ok && resetPasswordData.msg === "Password Reset Successful") {
        setStep("completed"); // Transition to Step 4 (Completion)
        onError(""); // Clear previous error message on success
      } else {
        // Handle specific error messages from the API, including cases where response.ok is false
        const apiErrorMessage = resetPasswordData.msg || `비밀번호 변경 중 오류 발생 (${resetPasswordResponse.status})`;

        if (apiErrorMessage === "입력된 비밀번호가 일치하지 않습니다.") {
          setPasswordMismatch(true); // Set client-side mismatch state if server also reports mismatch
          onError(apiErrorMessage);
        } else if (apiErrorMessage === "bad request") {
          onError("비밀번호 변경 요청이 올바르지 않습니다. 다시 시도해주세요.");
        } else if (apiErrorMessage === "Password Reset failed") {
          onError("비밀번호 변경에 실패했습니다. 다시 시도해주세요.");
        } else {
          // Generic error for other unexpected messages
          onError(apiErrorMessage);
        }
        console.error("resetPassword API 오류:", resetPasswordResponse.status, apiErrorMessage);
      }
    } catch (error) {
      console.error("비밀번호 변경 API 호출 중 예외 발생:", error);
      onError("비밀번호 변경 중 알 수 없는 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }

  // Render UI based on the current step
  return (
    <ModalWrapper handleDismiss={handleDismiss} className="PasswordReset-Window">
      <div className="PasswordReset-Wrapper">
        <div className="PasswordReset-Title">
          <img src={GameLogo} alt="Logo" />
        </div>
        <div className="PasswordReset-Content">
          {step === "inputUserEmail" && (
            <form onSubmit={handleUserEmailSubmit}>
              <input type="text" name="username" className={`Form-Input ${invalidUsername ? "error" : ""}`} placeholder="아이디" disabled={loading} value={username} onChange={handleUsernameChange} />
              {invalidUsername && <div className="error-text">아이디를 입력하세요.</div>}
              <input type="text" name="email" className={`Form-Input ${invalidEmail ? "error" : ""}`} placeholder="이메일 주소" disabled={loading} value={email} onChange={handleEmailChange} />
              {invalidEmail && email.length > 0 && <div className="error-text">유효한 이메일 주소를 입력하세요.</div>}
              <button type="submit" className="Form-Submit" disabled={loading || !username.trim() || !email || !validateEmail(email) || cooldown > 0}>
                {loading ? "확인 중..." : cooldown > 0 ? `${cooldown}초 후 재시도` : "인증번호 받기"}
              </button>
              <p style={{ textAlign: "center", fontSize: "12px", color: "#888", margin: "10px 0 0" }}>{MAIL_LIMIT_NOTICE}</p>
            </form>
          )}

          {step === "inputCode" && (
            <form onSubmit={handleCodeSubmit}>
              <input type="text" name="code" className="Form-Input" placeholder="인증번호 6자리 입력" value={code} onChange={handleCodeChange} disabled={loading || (timer !== null && timer <= 0)} />
              {/* Removed the separate timer-display div */}
              <button type="submit" className="Form-Submit" disabled={loading || !code.trim() || (timer !== null && timer <= 0)}>
                {/* Integrated timer display into button text */}
                {loading ? "확인 중..." : timer !== null && timer > 0 ? `${Math.floor(timer / 60)}:${timer % 60 < 10 ? "0" : ""}${timer % 60} 남음` : timer !== null && timer <= 0 ? "시간 만료" : "인증 코드 확인"}
              </button>
            </form>
          )}

          {step === "inputNewPassword" && (
            <form onSubmit={handlePasswordSubmit}>
              <input type="password" name="newPassword" className={`Form-Input ${invalidNewPassword || (passwordMismatch && !invalidNewPassword) ? "error" : ""}`} placeholder="새 비밀번호" disabled={loading} value={newPassword} onChange={handleNewPasswordChange} />
              {invalidNewPassword && <div className="error-text">새 비밀번호를 입력하세요.</div>}
              <input type="password" name="confirmNewPassword" className={`Form-Input ${invalidConfirmNewPassword || (passwordMismatch && !invalidConfirmNewPassword) ? "error" : ""}`} placeholder="새 비밀번호 확인" disabled={loading} value={confirmNewPassword} onChange={handleConfirmNewPasswordChange} />
              {invalidConfirmNewPassword && <div className="error-text">새 비밀번호 확인을 입력하세요.</div>}
              {passwordMismatch && <div className="error-text">입력된 비밀번호가 일치하지 않습니다.</div>}
              <button type="submit" className="Form-Submit" disabled={loading || !newPassword.trim() || !confirmNewPassword.trim() || newPassword !== confirmNewPassword}>
                {loading ? "변경 중..." : "비밀번호 변경"}
              </button>
            </form>
          )}

          {step === "completed" && (
            <section className="PasswordReset-Success-Wrapper" style={{ height: "auto" }}>
              <h2 className="PasswordReset-Success-Title">비밀번호 재설정이 완료되었습니다.</h2>
              <AnimatedCheckmark />
              <p className="PasswordReset-Success">이제 새로운 비밀번호로 로그인할 수 있습니다.</p>
              <p style={{ textAlign: "center", fontSize: "16px" }}>
                <div className="login-button">
                  <br />
                  <a onClick={openLoginWindow} style={{ cursor: "pointer" }}>
                    로그인 창으로 돌아가기
                  </a>
                </div>
              </p>
            </section>
          )}
        </div>
      </div>
      {loading && <LoadingIcon dim />}
    </ModalWrapper>
  );
}
